import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';

const Field = ({ label, k, creds, onChange, type = 'text', placeholder, help, required, cols = 6, secretSet }) => (
  <Col xs={cols}>
    <Form.Group>
      <Form.Label>{label}{required ? <span className='text-danger'> *</span> : null}</Form.Label>
      <Form.Control
        size='sm'
        type={type}
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        placeholder={placeholder || (type === 'password' && secretSet ? 'Leave blank to keep the stored value' : undefined)}
        value={creds[k] == null ? '' : String(creds[k])}
        onChange={(e) => onChange(k, e.target.value)}
        data-testid={`tdm-staging-${k}`}
      />
      {help && <Form.Text className='text-muted'>{help}</Form.Text>}
    </Form.Group>
  </Col>
);

/**
 * Per-type staging credential fields in the platform's data-source vocabulary (S3: region,
 * accessKeyId, secretAccessKey; Azure: accountKey | sasToken | connectionString | service principal;
 * SFTP: host, port, username, password | privateKey). `secretKeys` lists the secrets already stored
 * so a blank password field means "keep".
 */
const StagingCredentialFields = ({ type, credentialMode, creds, onChange, secretKeys }) => {
  const has = (k) => Array.isArray(secretKeys) && secretKeys.includes(k);
  const chain = credentialMode === 'DEFAULT_CHAIN';
  if (type === 'S3') {
    return (
      <>
        <Row className='text-left mb-2'>
          <Field label='AWS Region' k='region' creds={creds} onChange={onChange} placeholder='us-east-1' required />
          <Field label='Endpoint URL' k='endpointUrl' creds={creds} onChange={onChange} help='Only for S3-compatible stores (MinIO)' />
        </Row>
        {!chain && (
          <Row className='text-left mb-2'>
            <Field label='Access Key ID' k='accessKeyId' creds={creds} onChange={onChange} type='password' secretSet={has('accessKeyId')} />
            <Field label='Secret Access Key' k='secretAccessKey' creds={creds} onChange={onChange} type='password' secretSet={has('secretAccessKey')} />
          </Row>
        )}
        <Row className='text-left mb-2'>
          <Field label='Role ARN' k='roleArn' creds={creds} onChange={onChange} help='Optional — assume this role before writing' cols={12} />
        </Row>
      </>
    );
  }
  if (type === 'AZURE_BLOB') {
    return (
      <>
        {!chain && (
          <>
            <Row className='text-left mb-2'>
              <Field label='Account Key' k='accountKey' creds={creds} onChange={onChange} type='password' secretSet={has('accountKey')} />
              <Field label='SAS Token' k='sasToken' creds={creds} onChange={onChange} type='password' secretSet={has('sasToken')} help='Alternative to the account key' />
            </Row>
            <Row className='text-left mb-2'>
              <Field label='Tenant ID' k='tenantId' creds={creds} onChange={onChange} cols={4} />
              <Field label='Client ID' k='clientId' creds={creds} onChange={onChange} cols={4} />
              <Field label='Client Secret' k='clientSecret' creds={creds} onChange={onChange} type='password' cols={4} secretSet={has('clientSecret')} />
            </Row>
          </>
        )}
        <Row className='text-left mb-2'>
          <Field label='Endpoint URL' k='endpointUrl' creds={creds} onChange={onChange} help='Only for Azurite / sovereign clouds' cols={12} />
        </Row>
      </>
    );
  }
  return (
    <>
      <Row className='text-left mb-2'>
        <Field label='SFTP host' k='host' creds={creds} onChange={onChange} placeholder='sftp.example.com' required cols={8} />
        <Field label='Port' k='port' creds={creds} onChange={onChange} placeholder='22' cols={4} />
      </Row>
      <Row className='text-left mb-2'>
        <Field label='Username' k='username' creds={creds} onChange={onChange} required />
        <Field label='Password' k='password' creds={creds} onChange={onChange} type='password' secretSet={has('password')} />
      </Row>
      <Row className='text-left mb-2'>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Private key (PEM)</Form.Label>
            <Form.Control as='textarea' rows={3} size='sm' autoComplete='off'
              placeholder={has('privateKey') ? 'Leave blank to keep the stored key' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
              value={creds.privateKey || ''} onChange={(e) => onChange('privateKey', e.target.value)} data-testid='tdm-staging-privateKey' />
            <Form.Text className='text-muted'>Alternative to the password. Stored encrypted; never shown again.</Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default StagingCredentialFields;
